import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';
import { PricingCalculateResponse } from './pricing';

type ChannelResult = z.infer<typeof PricingCalculateResponse>['channels'][number];
const ChannelResultSchema = PricingCalculateResponse.shape.channels.element;

export const PriceCell = z.object({
  channelId: Uuid,
  listedPriceCentavos: z.number().int().nullable().describe('"Preço anunciado"; null = not set.'),
  result: ChannelResultSchema.nullable().describe(
    'Engine result for this channel with today’s costs: suggestion, and the evaluation at the listed price when set.',
  ),
});
export type PriceCell = z.infer<typeof PriceCell> & { result: ChannelResult | null };

export const ProductPricing = z
  .object({
    channels: z.array(z.object({ id: Uuid, name: z.string(), color: z.string() })),
    targetMarginBps: z.number().int().nullable(),
    minimumMarginBps: z.number().int().nullable(),
    rows: z.array(
      z.object({
        variant: z.object({ id: Uuid, name: z.string(), sku: z.string() }),
        unitCostCentavos: z.number().int().nullable(),
        perUnit: z.object({ grams: z.number(), printSeconds: z.number().int() }).nullable(),
        missing: z.string().nullable(),
        cells: z.array(PriceCell),
      }),
    ),
  })
  .meta({ id: 'ProductPricing' });
export type ProductPricing = z.infer<typeof ProductPricing>;

export const SetListedPriceBody = z.object({
  listedPriceCentavos: z
    .number()
    .int()
    .min(1)
    .max(100_000_000)
    .nullable()
    .describe('null removes it.'),
});

export const PriceCheckBody = z.object({
  channelId: Uuid,
  priceCentavos: z.number().int().min(1).max(100_000_000),
});
export const PriceCheckResult = z.object({
  unitCostCentavos: z.number().int(),
  result: ChannelResultSchema,
});

const ListingFields = z.object({
  channelId: Uuid,
  title: z.string().trim().min(1, 'Dê um título ao anúncio').max(60, 'Até 60 caracteres'),
  externalId: z
    .string()
    .trim()
    .max(80)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v))
    .describe('Listing ID on the channel (e.g. MLB…).'),
  url: z
    .string()
    .trim()
    .max(500)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
  status: z.enum(['active', 'paused']).default('active'),
  variantIds: z
    .array(Uuid)
    .max(100)
    .default([])
    .describe('Variants the listing sells; empty = all.'),
  externalRef: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
});
export const CreateListingBody = ListingFields.meta({
  id: 'CreateListingBody',
  example: {
    channelId: '00000000-0000-4000-8000-000000000001',
    title: 'Vaso Decorativo Ondas Impresso 3D',
  },
});
export const UpdateListingBody = patchOf(ListingFields);

export const Listing = z
  .object({
    id: Uuid,
    productId: Uuid,
    channel: z.object({ id: Uuid, name: z.string(), color: z.string() }),
    title: z.string(),
    externalId: z.string().nullable(),
    url: z.string().nullable(),
    status: z.enum(['active', 'paused']),
    variants: z.array(
      z.object({
        id: Uuid,
        name: z.string(),
        listedPriceCentavos: z.number().int().nullable().describe('From the Preços table.'),
      }),
    ),
    externalRef: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Listing' });
export type Listing = z.infer<typeof Listing>;
export const ListingList = z.object({ data: z.array(Listing) });
